"use client";

import { c as _c } from "react/compiler-runtime";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { DropInteractionParams, DropLocation } from "../Drop";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useCallback, useState } from "react";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import WaveDropActions from "../WaveDropActions";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import ParticipationDropContainer from "./ParticipationDropContainer";
import ParticipationDropHeader from "./ParticipationDropHeader";
import ParticipationDropContent from "./ParticipationDropContent";
import ParticipationDropMetadata from "./ParticipationDropMetadata";
import ParticipationDropFooter from "./ParticipationDropFooter";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
interface OngoingParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function OngoingParticipationDrop(t0) {
  const $ = _c(52);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick
  } = t0;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  let t1;
  if ($[0] !== hasTouch) {
    t1 = () => {
      if (!hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[0] = hasTouch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleLongPress = t1;
  let t2;
  if ($[2] !== activePartIndex || $[3] !== drop || $[4] !== onReply) {
    t2 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[2] = activePartIndex;
    $[3] = drop;
    $[4] = onReply;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleOnReply = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsSlideUp(false);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleOnAddReaction = t3;
  let t4;
  if ($[7] !== activePartIndex || $[8] !== drop || $[9] !== handleOnReply || $[10] !== isMobile || $[11] !== showReplyAndQuote) {
    t4 = !isMobile && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[7] = activePartIndex;
    $[8] = drop;
    $[9] = handleOnReply;
    $[10] = isMobile;
    $[11] = showReplyAndQuote;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== drop) {
    t5 = <WaveDropAuthorPfp drop={drop} />;
    $[13] = drop;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== drop || $[16] !== showWaveInfo) {
    t6 = <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} />;
    $[15] = drop;
    $[16] = showWaveInfo;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== activePartIndex || $[19] !== drop || $[20] !== handleLongPress || $[21] !== onDropContentClick || $[22] !== onQuoteClick) {
    t7 = <ParticipationDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} />;
    $[18] = activePartIndex;
    $[19] = drop;
    $[20] = handleLongPress;
    $[21] = onDropContentClick;
    $[22] = onQuoteClick;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t6 || $[25] !== t7) {
    t8 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-1.5">{t6}{t7}</div>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t5 || $[28] !== t8) {
    t9 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-px-4 tw-pt-4 tw-text-left">{t5}{t8}</div>;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== drop.id || $[31] !== drop.metadata) {
    t10 = <ParticipationDropMetadata metadata={drop.metadata} contextId={drop.id} />;
    $[30] = drop.id;
    $[31] = drop.metadata;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== drop) {
    t11 = <ParticipationDropFooter drop={drop} />;
    $[33] = drop;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11) {
    t12 = <div className="tw-flex tw-w-full tw-flex-col">{t10}{t11}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== drop || $[39] !== handleOnReply || $[40] !== isSlideUp || $[41] !== longPressTriggered || $[42] !== showReplyAndQuote) {
    t13 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[38] = drop;
    $[39] = handleOnReply;
    $[40] = isSlideUp;
    $[41] = longPressTriggered;
    $[42] = showReplyAndQuote;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== drop || $[45] !== isActiveDrop || $[46] !== location || $[47] !== t12 || $[48] !== t13 || $[49] !== t4 || $[50] !== t9) {
    t14 = <ParticipationDropContainer drop={drop} isActiveDrop={isActiveDrop} location={location}>{t4}{t9}{t12}{t13}</ParticipationDropContainer>;
    $[44] = drop;
    $[45] = isActiveDrop;
    $[46] = location;
    $[47] = t12;
    $[48] = t13;
    $[49] = t4;
    $[50] = t9;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  return t14;
}